import { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Loader2, CheckCircle2, AlertTriangle, Database } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ClassificationProgressTracker
 * 
 * Polls GET /api/bank-accounts/{id}/classification-progress every 3 seconds
 * and displays real-time progress for backlog transaction processing.
 * 
 * Shows on: Bank Transactions Center, My Banks, Settings > Banking
 * 
 * Props:
 *   bankAccountId - ID of the bank account to track
 *   bankName - Display name of the bank (optional)
 *   onComplete - Callback when processing finishes (optional)
 *   compact - If true, renders a smaller inline version
 */
export function ClassificationProgressTracker({ bankAccountId, bankName, onComplete, compact = false, enabled = true }) {
  const [progress, setProgress] = useState(null);
  const [visible, setVisible] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(null);
  const intervalRef = useRef(null);
  const completedRef = useRef(false);
  // Capture latest onComplete in a ref so it isn't a dep of fetchProgress
  // (otherwise every parent re-render rebuilds fetchProgress → useEffect
  // tears down + recreates the interval, causing a request storm).
  const onCompleteRef = useRef(onComplete);
  useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);

  const fetchProgress = useCallback(async () => {
    if (!bankAccountId || completedRef.current) return;
    try {
      const res = await fetch(`${API_URL}/api/bank-accounts/${bankAccountId}/classification-progress`, {
        credentials: 'include',
      });
      if (!res.ok) return;
      const data = await res.json();
      setProgress(data);

      const status = data?.status || data?.progress?.status;
      setCurrentStatus(status);
      if (status === 'processing' || status === 'queued') {
        setVisible(true);
      } else if (status === 'completed') {
        setVisible(true);
        completedRef.current = true;
        if (intervalRef.current) clearInterval(intervalRef.current);
        intervalRef.current = null;
        // Auto-hide after 10 seconds
        setTimeout(() => setVisible(false), 10000);
        onCompleteRef.current?.();
      } else if (status === 'error' || status === 'balance_gap') {
        setVisible(true);
        if (intervalRef.current) clearInterval(intervalRef.current);
        intervalRef.current = null;
      } else {
        // Idle / no active classification - stop polling entirely.
        setVisible(false);
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
    } catch (e) {
      // Silent - don't break the page
    }
  }, [bankAccountId]);

  // Single source of truth: one initial fetch on mount; the interval only
  // runs while currentStatus is in active states. No polling for idle or
  // completed accounts.
  useEffect(() => {
    if (!enabled || !bankAccountId) return undefined;
    fetchProgress();
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankAccountId, enabled]);

  useEffect(() => {
    if (!enabled || !bankAccountId) return;
    const isActive = currentStatus === 'processing' || currentStatus === 'queued';
    if (isActive && !intervalRef.current) {
      intervalRef.current = setInterval(fetchProgress, 3000);
    } else if (!isActive && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, [currentStatus, enabled, bankAccountId, fetchProgress]);

  if (!visible || !progress) return null;

  const p = progress.progress || {};
  // Sep 2026 · Mono progress is shown ONLY by the green BacklogProgressBanner.
  if (p.source === 'mono') return null;
  const status = progress.status || p.status || 'idle';
  const percentage = p.percentage || 0;
  const phase = p.phase || '';
  const total = p.total || 0;
  const completed = p.completed || 0;
  const result = p.result || {};
  const displayName = bankName || progress.bank_name || 'Bank Account';

  if (compact) {
    return (
      <div
        data-testid="classification-progress-compact"
        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/50 text-sm"
      >
        {status === 'processing' || status === 'queued' ? (
          <>
            <Loader2 className="h-3.5 w-3.5 animate-spin text-[#108981]" />
            <span className="text-muted-foreground truncate">{phase || 'Processing...'}</span>
            {total > 0 && <span className="text-xs font-medium ml-auto">{percentage}%</span>}
          </>
        ) : status === 'completed' ? (
          <>
            <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
            <span className="text-green-700 truncate text-xs">{result.auto_posted || 0} posted, {result.review_needed || 0} to review</span>
          </>
        ) : status === 'error' ? (
          <>
            <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
            <span className="text-red-600 truncate text-xs">{phase}</span>
          </>
        ) : null}
      </div>
    );
  }

  return (
    <Card data-testid="classification-progress-tracker" className="border-[#108981]/20 bg-[#108981]/[0.03]">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg shrink-0 ${
            status === 'error' ? 'bg-red-100' :
            status === 'balance_gap' ? 'bg-amber-100' :
            status === 'completed' ? 'bg-green-100' : 'bg-[#108981]/10'
          }`}>
            {status === 'processing' || status === 'queued' ? (
              <Database className="h-4 w-4 text-[#108981] animate-pulse" />
            ) : status === 'completed' ? (
              <CheckCircle2 className="h-4 w-4 text-green-600" />
            ) : status === 'error' ? (
              <AlertTriangle className="h-4 w-4 text-red-500" />
            ) : status === 'balance_gap' ? (
              <AlertTriangle className="h-4 w-4 text-amber-600" data-testid="classification-balance-gap-icon" />
            ) : (
              <Database className="h-4 w-4 text-muted-foreground" />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <p className="text-sm font-semibold text-foreground">
                {status === 'completed' ? 'Processing Complete' :
                 status === 'error' ? 'Processing Error' :
                 status === 'balance_gap' ? `${displayName}: balance gap - transactions parked` :
                 `Processing ${displayName} Transactions`}
              </p>
              {(status === 'processing' || status === 'queued') && (
                <Loader2 className="h-3.5 w-3.5 animate-spin text-[#108981] shrink-0" />
              )}
            </div>

            <p className={`text-xs mb-2 ${status === 'balance_gap' ? 'text-amber-800' : 'text-muted-foreground'}`} data-testid="classification-progress-phase">{phase}</p>

            {(status === 'processing' || status === 'queued') && total > 0 && (
              <>
                <Progress value={percentage} className="h-2 mb-1.5" />
                <div className="flex justify-between text-[10px] text-muted-foreground">
                  <span>{completed.toLocaleString()} of {total.toLocaleString()} transactions</span>
                  <span>{percentage}%</span>
                </div>
              </>
            )}

            {status === 'completed' && result && (
              <div className="flex gap-4 mt-1 text-xs">
                <span className="text-green-700 font-medium">{(result.auto_posted || 0).toLocaleString()} auto-posted</span>
                {result.review_needed > 0 && (
                  <span className="text-amber-600 font-medium">{result.review_needed.toLocaleString()} need review</span>
                )}
                {result.duplicates > 0 && (
                  <span className="text-muted-foreground">{result.duplicates.toLocaleString()} duplicates skipped</span>
                )}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
